import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageWrapper } from '../../components/layout';
import { OrgSubnav } from '../../components/shell/OrgSubnav';
import { LedgerSkeleton } from '../../components/shell/LedgerSkeleton';
import { BulkBar, LedgerTree, UserPeek, UsersTable, UsersViews } from '../../components/organizations-ledger';
import type { OrgNodeRef } from '../../components/organizations-ledger';
import {
  DefaultOrgDialog,
  InviteUserDialog,
  ProfileEditDialog,
  RoleAssignmentDialog,
} from '../../components/organizations';
import { LedgerDialog, LedgerField, LedgerSelect, LedgerSelectOption, LedgerSheet } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import {
  CreateUserRolePayload,
  UserListParams,
  useCreateUserRole,
  useDeleteUserRole,
  useOrgTreeData,
  useUserDetail,
  useUsersPage,
} from '../../hooks/useUserQueries';
import { useLedgerListKeys } from '../../hooks/useLedgerListKeys';
import { usePermissions } from '../../hooks/usePermissions';
import { useAppSelector } from '../../hooks/redux';
import { ManagedUser } from '../../types/auth.types';
import { UserRole } from '../../types/organization.types';
import { cn } from '../../lib/cn';

type RoleValue = 'ADMIN' | 'MANAGER' | 'USER';

const DROP_ROLE_OPTIONS: LedgerSelectOption[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'USER', label: 'User' },
];

// A user role binds exactly one org level, so the drop payload carries the
// dropped node's level and nothing else.
const bindingOf = (node: OrgNodeRef): Pick<CreateUserRolePayload, 'company' | 'team' | 'department'> =>
  node.type === 'company' ? { company: node.id } : node.type === 'team' ? { team: node.id } : { department: node.id };

// Historical filename: this file keeps its UserManagementHubPage name (and the
// route export alias at the bottom) from the pre-rebuild hub so the
// /organizations/users route wiring stays untouched. The component itself is
// the three-pane user management page: tree rail · table · peek rail.
export const UserManagementPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<OrgNodeRef | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [peekOpen, setPeekOpen] = useState(false);
  const [treeOpen, setTreeOpen] = useState(true);
  const [treeSheetOpen, setTreeSheetOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingName, setEditingName] = useState<ManagedUser | null>(null);
  const [roleDialog, setRoleDialog] = useState<{ user: ManagedUser; role: UserRole | null } | null>(null);
  const [removing, setRemoving] = useState<{ user: ManagedUser; role: UserRole } | null>(null);
  const [defaultsUser, setDefaultsUser] = useState<ManagedUser | null>(null);
  const [dropAssign, setDropAssign] = useState<{ user: ManagedUser; node: OrgNodeRef } | null>(null);
  const [dropRole, setDropRole] = useState<RoleValue>('USER');
  const [dropError, setDropError] = useState<string | null>(null);
  const toast = useToast();

  const { isStaff, isSuperuser } = usePermissions();
  const canAdmin = isStaff || isSuperuser;
  // The logged-in user, for the peek's isSelf guard.
  const currentUser = useAppSelector((state) => state.auth.user);

  // All three org keys derive fresh from the selected tree node — exactly one
  // set, the other two explicitly null — so no stale filter from a previous
  // scope can survive a scope switch.
  const params: UserListParams = useMemo(() => {
    const base = { search, activeOnly, page };
    switch (scope?.type) {
      case 'company':
        return { ...base, company: scope.id, team: null, department: null };
      case 'team':
        return { ...base, company: null, team: scope.id, department: null };
      case 'department':
        return { ...base, company: null, team: null, department: scope.id };
      default:
        return { ...base, company: null, team: null, department: null };
    }
  }, [search, activeOnly, page, scope]);

  const { data, isPending, isError } = useUsersPage(params);
  const rows = useMemo(() => data?.results ?? [], [data]);
  const count = data?.count ?? 0;

  // The tree endpoint walks the whole accessible hierarchy server-side; the
  // page-level mount is the fetch gate — it only runs while this page renders.
  const tree = useOrgTreeData(true);

  useEffect(() => {
    if (isError) toast.error('Failed to load users');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // Tailwind's lg breakpoint; the tree and peek are rails on desktop and
  // bottom sheets on mobile, and the sheets portal to <body>, so CSS can't
  // gate them.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // The desktop drop confirm has no mobile anchor until the tree sheet
  // reopens; crossing below lg with one pending would strand invisible dialog
  // state (and keep the list keys disabled). Drop it on the way down.
  useEffect(() => {
    if (!isDesktop) {
      setDropAssign(null);
      setDropError(null);
    }
  }, [isDesktop]);

  const selectedUser = rows.find((user) => user.id === selectedId) ?? null;
  // A selected user can leave the current page (filter or page change); fetch
  // them by id so the peek survives.
  const peekFallback = useUserDetail(peekOpen && selectedUser == null ? selectedId : null);
  const peekUser = selectedUser ?? peekFallback.data ?? null;

  // Selected rows can page out from under the id Set; remember every row this
  // session has seen so BulkBar can still name and operate on off-page picks.
  const seenUsers = useRef(new Map<string, ManagedUser>());
  useEffect(() => {
    for (const user of rows) seenUsers.current.set(user.id, user);
  }, [rows]);
  const selectedUsers = Array.from(selectedIds, (id) => seenUsers.current.get(id)).filter(
    (user): user is ManagedUser => user != null,
  );

  const move = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      const index = rows.findIndex((user) => user.id === selectedId);
      const from = index < 0 ? -delta : index;
      const next = Math.min(rows.length - 1, Math.max(0, from + delta));
      setSelectedId(rows[next].id);
    },
    [rows, selectedId],
  );
  const anyDialogOpen =
    inviteOpen ||
    editingName != null ||
    roleDialog != null ||
    removing != null ||
    defaultsUser != null ||
    dropAssign != null;
  useLedgerListKeys({
    enabled: !anyDialogOpen,
    onDown: () => move(1),
    onUp: () => move(-1),
    onPeek: () => setPeekOpen(selectedId != null),
    onClose: () => setPeekOpen(false),
  });

  // Filter/scope changes drop the selection: a Set built under one scope is
  // confusing (and dangerous to bulk-operate on) under another.
  const clearSelection = () => setSelectedIds(new Set());
  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
    clearSelection();
  };
  const changeActiveOnly = (value: boolean) => {
    setActiveOnly(value);
    setPage(1);
    clearSelection();
  };
  const selectScope = (node: OrgNodeRef | null) => {
    setScope(node);
    setPage(1);
    clearSelection();
    setTreeSheetOpen(false);
  };
  const resetView = () => {
    setSearch('');
    setActiveOnly(false);
    setScope(null);
    setPage(1);
    clearSelection();
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleSelectPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = rows.length > 0 && rows.every((user) => next.has(user.id));
      if (allSelected) rows.forEach((user) => next.delete(user.id));
      else rows.forEach((user) => next.add(user.id));
      return next;
    });

  // Drag side of the row-to-tree assignment contract: LedgerTree.onUserDrop
  // reads exactly this 'text/plain' key back out of the dataTransfer on drop.
  const handleRowDragStart = (event: React.DragEvent, user: ManagedUser) => {
    event.dataTransfer.setData('text/plain', user.id);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleUserDrop = (userId: string, node: OrgNodeRef) => {
    // The drag started from a rendered row, but a mid-drag refetch (refocus
    // revalidation, an invalidation) can page that row out before the drop
    // lands; seenUsers still holds the immutable id/email/fullName the
    // confirm needs.
    const user = rows.find((row) => row.id === userId) ?? seenUsers.current.get(userId);
    if (!user) return;
    setDropRole('USER');
    setDropError(null);
    setDropAssign({ user, node });
  };

  const createUserRole = useCreateUserRole();
  const confirmDropAssign = async () => {
    if (!dropAssign) return;
    setDropError(null);
    try {
      await createUserRole.mutateAsync({
        email: dropAssign.user.email,
        role: dropRole,
        ...bindingOf(dropAssign.node),
      });
      setDropAssign(null);
      toast.success('Role assigned');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setDropError(detail || 'Assign failed');
    }
  };

  const deleteUserRole = useDeleteUserRole();
  const confirmRemoveRole = () => {
    if (!removing) return;
    const target = removing;
    // Close synchronously so a second click can't queue a duplicate delete.
    setRemoving(null);
    deleteUserRole.mutate(target.role.id, {
      onSuccess: () => toast.success('Role removed'),
      onError: () => toast.error('Remove failed'),
    });
  };

  const dropCancelRef = useRef<HTMLButtonElement>(null);
  const dropConfirm = (
    <LedgerDialog
      open={dropAssign != null}
      onOpenChange={(next) => {
        if (!next) setDropAssign(null);
      }}
      title={dropAssign ? `Assign to ${dropAssign.node.name}` : 'Assign role'}
      description={dropAssign ? `${dropAssign.user.fullName} → ${dropAssign.node.name}` : undefined}
      size="sm"
      preventClose={createUserRole.isPending}
      initialFocus={dropCancelRef}
      footer={
        <>
          <button
            type="button"
            ref={dropCancelRef}
            onClick={() => setDropAssign(null)}
            disabled={createUserRole.isPending}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDropAssign}
            disabled={createUserRole.isPending}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            Assign
          </button>
        </>
      }
    >
      <>
        {dropError && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {dropError}
          </p>
        )}
        <LedgerField label="Role">
          <LedgerSelect
            ariaLabel="Role"
            value={dropRole}
            options={DROP_ROLE_OPTIONS}
            onValueChange={(value) => setDropRole(value as RoleValue)}
          />
        </LedgerField>
      </>
    </LedgerDialog>
  );

  const removeCancelRef = useRef<HTMLButtonElement>(null);
  const removeConfirm = (
    <LedgerDialog
      open={removing != null}
      onOpenChange={(next) => {
        if (!next) setRemoving(null);
      }}
      title="Remove role?"
      description={
        removing ? `${removing.role.role} · ${removing.role.organizationName} for ${removing.user.fullName}` : undefined
      }
      size="sm"
      preventClose={deleteUserRole.isPending}
      initialFocus={removeCancelRef}
      footer={
        <>
          <button
            type="button"
            ref={removeCancelRef}
            onClick={() => setRemoving(null)}
            disabled={deleteUserRole.isPending}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmRemoveRole}
            disabled={deleteUserRole.isPending}
            className="cursor-pointer bg-sev-critical px-3 py-1.5 text-scale-1 font-semibold text-paper hover:opacity-90 disabled:cursor-default disabled:opacity-60"
          >
            Remove
          </button>
        </>
      }
    >
      {null}
    </LedgerDialog>
  );

  const treeContent = tree.isPending ? (
    <LedgerSkeleton lines={6} className="mt-2" />
  ) : tree.isError ? (
    <p className="py-4 font-serif text-scale-1 italic text-ink-muted">Failed to load organizations.</p>
  ) : tree.data ? (
    <LedgerTree
      data={tree.data}
      selected={scope}
      onSelect={selectScope}
      // Drop targets only arm for admins — without the handler the tree
      // renders no drag-over affordance at all.
      onUserDrop={canAdmin ? handleUserDrop : undefined}
    />
  ) : null;

  const renderPeek = (user: ManagedUser) => (
    <UserPeek
      user={user}
      canAdmin={canAdmin}
      isSelf={currentUser?.id === user.id}
      onClose={() => setPeekOpen(false)}
      onEditName={setEditingName}
      onAddRole={(subject) => setRoleDialog({ user: subject, role: null })}
      onEditRole={(role) => setRoleDialog({ user, role })}
      onRemoveRole={(role) => setRemoving({ user, role })}
      onSetDefaults={setDefaultsUser}
    />
  );

  const peekRail = isDesktop && peekOpen && peekUser != null;
  const gridClass =
    treeOpen && peekRail
      ? 'lg:grid-cols-[260px_1fr_340px]'
      : treeOpen
        ? 'lg:grid-cols-[260px_1fr]'
        : peekRail
          ? 'lg:grid-cols-[1fr_340px]'
          : 'lg:grid-cols-[1fr]';

  return (
    <PageWrapper
      title="User Management"
      // Envelope count only — the tree's per-node numbers count distinct role
      // holders per subtree, a different measure than the filtered user list.
      subtitle={isPending ? undefined : `${count} user${count === 1 ? '' : 's'}`}
      noPaper
      maxWidth="xl"
      actions={
        canAdmin ? (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
          >
            + Invite user
          </button>
        ) : undefined
      }
    >
      <OrgSubnav />

      <div className="hidden md:block">
        <UsersViews
          search={search}
          activeOnly={activeOnly}
          onSearchChange={changeSearch}
          onActiveOnlyChange={changeActiveOnly}
        />
      </div>
      <div className="flex items-baseline justify-between pt-1">
        <span className="flex items-baseline gap-4">
          <button
            type="button"
            onClick={() => setTreeSheetOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={treeSheetOpen}
            className="cap-label cursor-pointer lg:hidden"
          >
            Tree <span aria-hidden>▾</span>
          </button>
          <button
            type="button"
            onClick={() => setTreeOpen((open) => !open)}
            aria-expanded={treeOpen}
            className="cap-label hidden cursor-pointer lg:inline"
          >
            {treeOpen ? 'Tree ⟨' : '⟩ Tree'}
          </button>
        </span>
        <button
          type="button"
          onClick={() => setFilterSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={filterSheetOpen}
          className="cap-label cursor-pointer md:hidden"
        >
          Filter <span aria-hidden>▾</span>
        </button>
      </div>
      <LedgerSheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen} title="Views & search">
        <UsersViews
          search={search}
          activeOnly={activeOnly}
          onSearchChange={changeSearch}
          onActiveOnlyChange={changeActiveOnly}
        />
      </LedgerSheet>
      <LedgerSheet open={treeSheetOpen && !isDesktop} onOpenChange={setTreeSheetOpen} title="Organizations">
        {treeContent}
        {/* Nested inside the sheet on purpose: sibling Base UI roots would
            both receive a single Escape and close together. */}
        {!isDesktop && dropConfirm}
      </LedgerSheet>

      {scope && (
        <div className="mt-2">
          <span className="cap-label inline-flex items-baseline gap-2 border border-rule-faint px-2 py-0.5">
            {scope.name}
            <button
              type="button"
              aria-label="Clear organization filter"
              onClick={() => selectScope(null)}
              className="cursor-pointer hover:!text-ink"
            >
              ✕
            </button>
          </span>
        </div>
      )}

      <div className={cn('mt-1', isDesktop && 'lg:grid lg:gap-x-5', isDesktop && gridClass)}>
        {isDesktop && treeOpen && (
          <aside aria-label="Organization tree" className="min-w-0 border-r border-rule-faint pr-3">
            {treeContent}
          </aside>
        )}
        <div className="min-w-0">
          <UsersTable
            rows={rows}
            count={count}
            page={page}
            isPending={isPending}
            hasNext={data?.next != null}
            hasPrevious={data?.previous != null}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setPeekOpen(true);
            }}
            onPageChange={setPage}
            onResetView={resetView}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectPage={toggleSelectPage}
            canAdmin={canAdmin}
            onRowDragStart={handleRowDragStart}
          />
        </div>
        {peekRail && peekUser && renderPeek(peekUser)}
      </div>

      <LedgerSheet
        open={peekOpen && !isDesktop && peekUser != null}
        onOpenChange={(open) => {
          if (!open) setPeekOpen(false);
        }}
        title="User"
      >
        {peekUser && (
          <>
            {renderPeek(peekUser)}
            {/* Nested inside the sheet on purpose: sibling Base UI roots would
                both receive a single Escape and close together. */}
            {!isDesktop && removeConfirm}
          </>
        )}
      </LedgerSheet>
      {isDesktop && removeConfirm}
      {isDesktop && dropConfirm}

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={(created) => {
          toast.success('Invitation sent');
          // A fresh invitee holds no role yet, and role-less users are
          // invisible to staff admins' scoped lists — chain straight into
          // granting the first role while the created record is still in hand.
          setRoleDialog({ user: created, role: null });
        }}
      />
      {editingName && (
        <ProfileEditDialog
          open
          onClose={() => setEditingName(null)}
          onSuccess={() => toast.success('Name updated')}
          user={editingName}
        />
      )}
      {roleDialog && (
        <RoleAssignmentDialog
          open
          onClose={() => setRoleDialog(null)}
          // Runs before the dialog's own close path, so `role` still tells
          // create and update apart here.
          onSuccess={() => toast.success(roleDialog.role ? 'Role updated' : 'Role assigned')}
          user={roleDialog.user}
          role={roleDialog.role}
        />
      )}
      {defaultsUser && (
        <DefaultOrgDialog
          open
          onClose={() => setDefaultsUser(null)}
          onSuccess={() => toast.success('Defaults saved')}
          user={defaultsUser}
        />
      )}

      {canAdmin && (
        <BulkBar selected={selectedUsers} onClear={clearSelection} onDone={(message) => toast.success(message)} />
      )}
    </PageWrapper>
  );
};

// Route-stable alias: App.tsx and pages/organizations/index.ts import the
// historical name.
export { UserManagementPage as UserManagementHubPage };
